import type { FantasyContest, FantasyTeam } from '@brush/api-client';
import { Spinner } from '@brush/ui';
import { useState } from 'react';
import {
  STATUS_LABEL,
  budgetUnit,
  formatAmount,
  formatLockTime,
  hasStandings,
  isOpenForEntry,
  statusOf,
} from '../../fantasy/roster';
import { usePlayer } from '../../player';
import { useFantasyContest, useMyFantasyTeams } from '../../queries';
import { ContestStandings } from './ContestStandings';
import { LineupBuilder } from './LineupBuilder';
import { TeamBreakdown } from './TeamBreakdown';

type Tab = 'lineup' | 'standings';

/**
 * One contest: what it asks of you, what you entered, and how it is going.
 *
 * <b>The contest is re-fetched here rather than taken from the list.</b> `standingsPaused` is
 * computed per read and the list's copy can be minutes old, so a screen that leaned on it could
 * show a board as healthy while it is frozen — which is the one thing §9.2 says must not happen.
 * The list's row is used until the fresh one lands, so nothing blanks.
 *
 * <b>Two tabs, and which one opens is decided by the contest rather than by a preference.</b>
 * Before the lock there is a lineup to build and no standings to read; after it the lineup is a
 * fact and the standings are the reason anybody came back.
 */
export function ContestScreen({
  contest: listed,
  onBack,
}: {
  contest: FantasyContest;
  onBack: () => void;
}) {
  const { theme } = usePlayer();
  const detail = useFantasyContest(listed.id);
  const contest = detail.data ?? listed;

  const teamsQ = useMyFantasyTeams(contest.id);
  const teams = teamsQ.data ?? [];
  const entry = teams[0] ?? null;

  const [tab, setTab] = useState<Tab>(() => (hasStandings(listed) ? 'standings' : 'lineup'));

  const status = statusOf(contest);
  const unit = budgetUnit(contest);
  const card = { background: theme.panel, border: `1px solid ${theme.border}` };

  return (
    <div className="space-y-5">
      <button
        type="button"
        onClick={onBack}
        className="text-xs font-semibold uppercase tracking-wider transition hover:opacity-80"
        style={{ color: theme.textMuted }}
      >
        ← All contests
      </button>

      <header>
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <h1 className="text-2xl font-bold">{contest.name}</h1>
          <span
            className="rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider"
            style={{ background: theme.border, color: theme.textMuted }}
          >
            {STATUS_LABEL[status]}
          </span>
        </div>
        {contest.description ? (
          <p className="mt-1 text-sm" style={{ color: theme.textMuted }}>
            {contest.description}
          </p>
        ) : null}
      </header>

      {/* §9.2's rules, budget, roster size and lock time — the four facts somebody needs before
          they decide whether to play, in one block rather than scattered through the builder. */}
      <section style={card} className="grid grid-cols-2 gap-4 rounded-xl p-5 sm:grid-cols-4">
        <Fact label="Your team">{contest.rosterSize} players</Fact>
        <Fact label="To spend">{formatAmount(contest.budget, unit)}</Fact>
        <Fact label="Captain">
          {contest.captainEnabled ? `Scores ×${contest.captainMultiplier ?? 1}` : 'Not used'}
        </Fact>
        <Fact label={status === 'open' ? 'Entries close' : 'Entries closed'}>
          {formatLockTime(status === 'open' ? contest.locksAt : (contest.lockedAt ?? contest.locksAt))}
        </Fact>
      </section>

      {/* Entry is free and one team each. Stated because "what does this cost me" is the first
          question anybody has about a contest, and §12 makes both answers permanent for now. */}
      <p className="text-xs" style={{ color: theme.textMuted }}>
        Free to enter · one team per person · {contest.entries === 1 ? '1 entry' : `${contest.entries} entries`} so far
      </p>

      {hasStandings(contest) ? (
        <div className="flex gap-2">
          {(['standings', 'lineup'] as const).map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => setTab(value)}
              className="rounded-lg px-3 py-2 text-sm font-medium transition"
              style={{
                background: tab === value ? `${theme.accent}1a` : theme.panel,
                color: tab === value ? theme.accent : theme.textMuted,
                border: `1px solid ${tab === value ? theme.accent : theme.border}`,
              }}
            >
              {value === 'standings' ? 'Standings' : 'My team'}
            </button>
          ))}
        </div>
      ) : null}

      {teamsQ.isLoading || detail.isLoading ? (
        <Spinner label="Loading your entry…" />
      ) : tab === 'standings' && hasStandings(contest) ? (
        <ContestStandings contest={contest} myTeam={entry} />
      ) : isOpenForEntry(contest) ? (
        <LineupBuilder contest={contest} entry={entry} />
      ) : (
        <LockedEntry contest={contest} entry={entry} />
      )}
    </div>
  );
}

/**
 * The lineup once it cannot change — and, once there is anything to show, why it scored what it
 * scored.
 *
 * A contest that is Not open yet lands here too: there is no pool to draft from and nothing
 * entered, and saying so beats an empty builder that looks broken.
 */
function LockedEntry({ contest, entry }: { contest: FantasyContest; entry: FantasyTeam | null }) {
  const { theme } = usePlayer();
  const card = { background: theme.panel, border: `1px solid ${theme.border}` };

  if (statusOf(contest) === 'draft') {
    return (
      <p style={card} className="rounded-xl p-8 text-center text-sm">
        <span style={{ color: theme.textMuted }}>
          This contest hasn’t opened for entries yet. Check back when the room publishes it.
        </span>
      </p>
    );
  }

  if (!entry) {
    return (
      <p style={card} className="rounded-xl p-8 text-center text-sm">
        <span style={{ color: theme.textMuted }}>
          You didn’t enter this one. Entries closed {formatLockTime(contest.lockedAt ?? contest.locksAt)}.
        </span>
      </p>
    );
  }

  return <TeamBreakdown contest={contest} team={entry} />;
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  const { theme } = usePlayer();
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wider" style={{ color: theme.textMuted }}>
        {label}
      </div>
      <div className="mt-0.5 text-sm font-semibold">{children}</div>
    </div>
  );
}
